import { useCallback, useState } from "react"
import { Box, Chip, Divider, IconButton, Switch, Tooltip, Typography } from "@mui/material"
import type { SxProps, Theme } from "@mui/material"
import { DeleteOutlined } from "@mui/icons-material"
import { useUserRole } from "hooks/users"
import type { SkillCatalogSkill } from "connected-components/skills/types"
import { canDeleteSkill, canDownloadSkill, canManageSkillShare, canShareSkill, canViewSkillFiles } from "connected-components/skills/utils/tier"
import { SkillDownloadAction } from "connected-components/skills/components/skill-download-action"
import { ConfirmActionDialog } from "connected-components/agent-chat/chatbox/confirm-action-dialog"
import { MessageTime } from "connected-components/agent-chat/chatbox/message-time"
import { SkillRunButton } from "./skill-run-button"
import type { SkillRunControl } from "./skill-run-button"
import { SkillShareButton } from "./skill-share-button"
import { SkillFilesTab } from "./skill-files-tab"
import { formatSharedBy, formatSkillMeta, tierLabel } from "./skill-meta"
import type { KellyPaneLayout } from "../kelly-pane-page"

const DETAIL_SX = { display: "flex", flexDirection: "column", gap: 2, minWidth: 0 } satisfies SxProps<Theme>
const HEADER_SX = { display: "flex", alignItems: "flex-start", gap: 2, flexWrap: "wrap" } satisfies SxProps<Theme>
const HEADING_BLOCK_SX = { display: "flex", flexDirection: "column", gap: 0.5, flex: 1, minWidth: 240 } satisfies SxProps<Theme>
const TITLE_SX = { display: "flex", alignItems: "baseline", gap: 1.5, flexWrap: "wrap", m: 0 } satisfies SxProps<Theme>
const PILL_ROW_SX = { display: "flex", alignItems: "center", flexWrap: "wrap", gap: 0.75 } satisfies SxProps<Theme>
// The tier is where the skill came from, not one of its tags, so it leads the row and carries a filled
// background the outlined category chips beside it do not. color-mix on --app-primary is scheme-aware.
const TIER_CHIP_SX = { bgcolor: "color-mix(in srgb, var(--app-primary) 14%, transparent)", color: "var(--app-text-primary)", fontWeight: 600 } satisfies SxProps<Theme>
const META_SX = { fontSize: "0.8125rem", color: "text.secondary" } satisfies SxProps<Theme>
const DESCRIPTION_SX = { color: "text.primary", mt: 1 } satisfies SxProps<Theme>
const SWITCH_ROW_SX = { display: "flex", alignItems: "center", gap: 1, ml: "auto" } satisfies SxProps<Theme>
const SWITCH_LABEL_SX = { fontSize: "0.875rem", fontWeight: 500, color: "text.secondary" } satisfies SxProps<Theme>

export interface SkillDetailProps {
  skill: SkillCatalogSkill
  layout: KellyPaneLayout
  enabled: boolean
  isSaving: boolean
  isDeleting: boolean
  onToggle: (skill: SkillCatalogSkill, enabled: boolean) => void
  onDelete: (skill: SkillCatalogSkill) => void
  run?: SkillRunControl
}

export function SkillDetail({ skill, layout, enabled, isSaving, isDeleting, onToggle, onDelete, run }: SkillDetailProps) {
  const role = useUserRole()
  const canManage = canManageSkillShare(skill, role)
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false)

  const openDeleteDialog = useCallback(() => setIsDeleteDialogOpen(true), [])
  const closeDeleteDialog = useCallback(() => setIsDeleteDialogOpen(false), [])
  const confirmDelete = useCallback(() => {
    setIsDeleteDialogOpen(false)
    onDelete(skill)
  }, [onDelete, skill])

  return (
    <Box sx={DETAIL_SX} data-testid="kelly-skill-detail">
      <Box sx={HEADER_SX}>
        <Box sx={HEADING_BLOCK_SX}>
          <Typography component="h3" variant="h6" sx={TITLE_SX}>
            {skill.name}
            {skill.beta && <Chip size="small" label="Preview" />}
          </Typography>
          <Typography component="span" sx={META_SX} data-testid="kelly-skill-detail-meta">
            {skill.tier === "shared"
              ? <>{formatSharedBy(skill)}{skill.lastUpdated && <>{" · "}<MessageTime date={skill.lastUpdated} /></>}</>
              : formatSkillMeta(skill, { withAuthor: true })}
          </Typography>
          <Box sx={PILL_ROW_SX}>
            <Chip size="small" label={tierLabel(skill.tier)} sx={TIER_CHIP_SX} data-testid="kelly-skill-tier" />
            {skill.categories?.map((category) => (
              <Chip key={category} size="small" variant="outlined" label={category} data-testid="kelly-skill-category" />
            ))}
          </Box>
          <Typography variant="body1" sx={DESCRIPTION_SX}>{skill.description}</Typography>
        </Box>
        <Box sx={SWITCH_ROW_SX}>
          {run != null && <SkillRunButton skill={skill} enabled={enabled} isSaving={isSaving} run={run} />}
          {canShareSkill(skill) && <SkillShareButton skill={skill} />}
          {canManage && <SkillShareButton skill={skill} mode="manage" />}
          {canDownloadSkill(skill) && <SkillDownloadAction skill={skill} noun="skill" />}
          {canDeleteSkill(skill) && (
            <Tooltip title="Delete skill">
              <IconButton aria-label={`Delete ${skill.name}`} onClick={openDeleteDialog} disabled={isDeleting}>
                <DeleteOutlined />
              </IconButton>
            </Tooltip>
          )}
          <Switch
            color="primary"
            checked={enabled}
            disabled={isSaving}
            onChange={(_, checked) => onToggle(skill, checked)}
            slotProps={{ input: { "aria-label": `Use ${skill.name}` } }}
          />
          <Typography component="span" sx={SWITCH_LABEL_SX}>{enabled ? "Enabled" : "Disabled"}</Typography>
        </Box>
      </Box>
      {canViewSkillFiles(skill) && (
        <>
          <Divider />
          <SkillFilesTab skill={skill} layout={layout} />
        </>
      )}
      <ConfirmActionDialog
        open={isDeleteDialogOpen}
        title={`Delete "${skill.name}"?`}
        body="This removes the skill for all users. This cannot be undone."
        confirmLabel="Delete"
        onClose={closeDeleteDialog}
        onConfirm={confirmDelete}
      />
    </Box>
  )
}
